import React, { useState, useMemo, useEffect } from 'react';
import { Search, X, ChevronRight, AlertCircle, Eye, ShoppingCart, Clock, Truck, CheckCircle, DollarSign } from 'lucide-react';
import { ORDER_STATUS, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../constants/status';

// 状态徽章组件
const StatusBadge = ({ status }) => {
  const config = ORDER_STATUS_COLORS[status];
  if (!config) return null;

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {ORDER_STATUS_LABELS.workbench[status] || status}
    </span>
  );
};

// 统计卡片组件
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);

// 状态筛选选项
const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: ORDER_STATUS.PENDING_SIGN, label: '待签署' },
  { value: ORDER_STATUS.PENDING_APPROVAL, label: '待审批' },
  { value: ORDER_STATUS.IN_PROGRESS, label: '进行中' },
  { value: ORDER_STATUS.REJECTED, label: '已驳回' },
  { value: ORDER_STATUS.COMPLETED, label: '已完成' },
];

// 主组件
export default function OrderView({ orders, onOrderClick, initialFilter, setInitialFilter }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterBase, setFilterBase] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');

  // 处理从首页传递的初始筛选条件
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status && initialFilter.status !== 'all') {
        setFilterStatus(initialFilter.status);
      }
      setInitialFilter?.(null); // 清除初始筛选条件，避免重复应用
    }
  }, [initialFilter, setInitialFilter]);

  // 计算统计数据
  const stats = useMemo(() => {
    const pendingApproval = orders.filter(o => o.status === ORDER_STATUS.PENDING_APPROVAL).length;
    const inProgress = orders.filter(o => o.status === ORDER_STATUS.IN_PROGRESS).length;
    const completed = orders.filter(o => o.status === ORDER_STATUS.COMPLETED).length;

    // 预估总金额
    const totalAmount = orders.reduce((sum, o) => sum + (o.estimatedAmount || 0), 0);

    // 等待审批的送货单数量
    const waitingApprovalCount = orders.reduce((sum, o) => sum + (o.deliveryStats?.waitingApproval || 0), 0);

    return {
      total: orders.length,
      pendingApproval,
      inProgress,
      completed,
      totalAmount,
      waitingApprovalCount,
    };
  }, [orders]);

  // 提取筛选选项
  const { suppliers, bases } = useMemo(() => {
    const supplierSet = new Set();
    const baseSet = new Set();

    orders.forEach(o => {
      if (o.supplierName) supplierSet.add(o.supplierName);
      if (o.baseName) baseSet.add(o.baseName);
    });

    return {
      suppliers: Array.from(supplierSet).sort(),
      bases: Array.from(baseSet).sort(),
    };
  }, [orders]);

  // 是否有活跃筛选
  const hasActiveFilters = searchQuery || filterStatus || filterSupplier || filterBase || filterDateStart || filterDateEnd;

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterSupplier('');
    setFilterBase('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // 筛选订单
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(o =>
        o.orderNo.toLowerCase().includes(query) ||
        o.supplierName.toLowerCase().includes(query) ||
        o.baseName.toLowerCase().includes(query) ||
        o.materialName.toLowerCase().includes(query)
      );
    }

    // 状态筛选
    if (filterStatus) {
      result = result.filter(o => o.status === filterStatus);
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(o => o.supplierName === filterSupplier);
    }

    // 基地筛选
    if (filterBase) {
      result = result.filter(o => o.baseName === filterBase);
    }

    // 日期范围筛选
    if (filterDateStart) {
      result = result.filter(o => o.createdAt && o.createdAt >= filterDateStart);
    }
    if (filterDateEnd) {
      result = result.filter(o => o.createdAt && o.createdAt <= filterDateEnd + ' 23:59:59');
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [orders, searchQuery, filterStatus, filterSupplier, filterBase, filterDateStart, filterDateEnd]);

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">订单管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有采购订单</p>
        </div>
        {/* 等待审批提醒 */}
        {stats.waitingApprovalCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle size={18} className="text-amber-500" />
            <span className="text-sm text-amber-700 font-medium">
              {stats.waitingApprovalCount} 辆车等待订单审批
            </span>
          </div>
        )}
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          title="全部订单"
          value={stats.total}
          icon={ShoppingCart}
          iconColor="text-slate-600"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="待审批"
          value={stats.pendingApproval}
          icon={Clock}
          iconColor="text-orange-600"
          bgColor="bg-orange-50"
        />
        <StatCard
          title="进行中"
          value={stats.inProgress}
          icon={Truck}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
        />
        <StatCard
          title="已完成"
          value={stats.completed}
          icon={CheckCircle}
          iconColor="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          title="预估总金额"
          value={`¥${stats.totalAmount.toLocaleString()}`}
          icon={DollarSign}
          iconColor="text-red-600"
          bgColor="bg-red-50"
        />
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索订单号、供应商、基地、物料..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {/* 供应商筛选 */}
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>

            {/* 基地筛选 */}
            <select
              value={filterBase}
              onChange={(e) => setFilterBase(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部基地</option>
              {bases.map(base => (
                <option key={base} value={base}>{base}</option>
              ))}
            </select>

            {/* 日期范围 */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <span className="text-slate-400">~</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 表格区域 */}
        {filteredOrders.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingCart size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">暂无订单</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">订单编号</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">供应商</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">物料</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">基地</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider text-right">预估重量</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider text-right">预估金额</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">状态</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">创建时间</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">送货进度</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredOrders.map(order => (
                  <tr
                    key={order.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="p-3">
                      <span className="font-mono text-xs text-slate-600">{order.orderNo}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-800 font-medium">{order.supplierName}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{order.materialName}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{order.baseName}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-sm font-medium text-slate-800">{order.estimatedWeight} 吨</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-sm font-bold text-red-600">¥{order.estimatedAmount?.toLocaleString()}</span>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-600">{order.createdAt || '-'}</span>
                    </td>
                    <td className="p-3">
                      {order.deliveryCount > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-400"
                              style={{ width: `${(order.deliveryStats?.paid || 0) / order.deliveryCount * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {order.deliveryStats?.paid || 0}/{order.deliveryCount}
                          </span>
                          {(order.deliveryStats?.waitingApproval || 0) > 0 && (
                            <AlertCircle size={14} className="text-amber-500" />
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => onOrderClick?.(order)}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Eye size={14} />
                        详情
                        <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部统计 */}
        {filteredOrders.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredOrders.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{orders.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
